import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';
import RichTextEditorModal from '../../components/RichTextEditorModal/RichTextEditorModal';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useProdutoService } from '../../services/produtoService';
import { useTesteService } from '../../services/testeServices';

// Estilos
import styles from './testeformpage.module.css';

// Assets
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

/** === Metadados dos tipos (escalável) ===
 *  Cores/labels centralizados: alterando aqui, a UI acompanha.
 */
const ORDERED_TYPES = ['closed', 'open', 'priority', 'scale'];
const TYPE_META = {
  closed: {
    label: 'Fechada',
    badgeText: 'FECHADA',
    border: '#855FDF',
    bg: '#ffffffff',
    badgeBg: '#855FDF',
  },
  open: {
    label: 'Aberta',
    badgeText: 'ABERTA',
    border: '#00C5B9',
    bg: '#ffffffff',
    badgeBg: '#00C5B9',
  },
  priority: {
    label: 'Prioridade',
    badgeText: 'PRIORIDADE',
    border: '#CD1870',
    bg: '#ffffffff',
    badgeBg: '#CD1870',
  },
  scale: {
    label: 'Escala',
    badgeText: 'ESCALA',
    border: '#041838',
    bg: '#ffffffff',
    badgeBg: '#041838',
  },
};

/** Config padrão para perguntas de escala */
const DEFAULT_SCALE = { min: 0, max: 10, labels: {} };

/** === Helpers de ID estável p/ perguntas === */
const genRandomId = () => {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}
  // Fallback: timestamp + random base36
  return `Q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};
const genUniqueId = (existing = new Set()) => {
  let id = genRandomId();
  while (existing.has(id)) id = genRandomId();
  return id;
};

// ==== Helpers de letras/ordem (para rótulos A..Z e consistência) ====
const reindexLetters = (opcoes) =>
  (opcoes || []).map((o, idx) => ({ ...o, letra: String.fromCharCode(65 + idx) }));

const nextAvailableLetter = (opcoes) => {
  const used = new Set((opcoes || []).map(o => o.letra));
  for (let i = 0; i < 26; i++) {
    const L = String.fromCharCode(65 + i);
    if (!used.has(L)) return L;
  }
  return null;
};

function TesteFormPage() {
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idTeste } = useParams();
  const { pathname } = useLocation();

  const isEditing = !!idTeste && pathname.includes('atualizar');

  const { listarProdutos } = useProdutoService();
  const { criarTeste, atualizarTeste, obterTestePorId, listarProdutosDoTeste } = useTesteService();

  const [buscaProduto, setBuscaProduto] = useState('');
  const [sugestoesFiltradas, setSugestoesFiltradas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    produtos: [],
    // perguntas: [{ id, enunciado, tipo, opcoes, scale, hasDate? }]
    perguntas: [],
  });

  const [scrollBottom, setScrollBottom] = useState(false);
  const [isDescriptionEditorOpen, setIsDescriptionEditorOpen] = useState(false);

  /** Retorna um Set com todos os IDs de perguntas atuais (para evitar colisão) */
  const currentQuestionIds = () => new Set((formData.perguntas || []).map(p => p.id).filter(Boolean));

  useEffect(() => {
    const carregarProdutos = async () => {
      showLoader("Carregando produtos");

      const response = await listarProdutos();
      if (response.success) {
        setProdutos(response.data);
      } else {
        showToast("Erro ao carregar produtos", 3000, 'error');
      }

      hideLoader();
    };

    carregarProdutos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    const carregarTeste = async () => {
      showLoader("Carregando teste");

      try {
        const [resTeste, resProdutos] = await Promise.all([
          obterTestePorId(idTeste),
          listarProdutosDoTeste(idTeste)
        ]);

        if (!resTeste.success) {
          showToast("Erro ao carregar o teste", 3000, 'error');
          navigate('/testes');
          return;
        }

        const estruturaParsed = (() => {
          try { return JSON.parse(resTeste.data.estrutura || '{}'); }
          catch { return {}; }
        })();

        // Colete IDs existentes para evitar colisão ao gerar IDs para legados
        const existingIds = new Set();

        const perguntas = (estruturaParsed.questions || []).map((q) => {
          // Garante ID estável (gera se legado)
          const qid = q.id && typeof q.id === 'string' && q.id.trim()
            ? q.id
            : genUniqueId(existingIds);
          existingIds.add(qid);

          // Normaliza opções como array [{letra, texto}] para closed/priority
          let opcoes = [];
          if (q.type === 'closed' || q.type === 'priority') {
            const entries = Object.entries(q.options || {});
            if (q.type === 'priority' && Array.isArray(q.order)) {
              const map = Object.fromEntries(entries);
              opcoes = q.order
                .filter(k => map[k] !== undefined)
                .map((k, idx) => ({ letra: String.fromCharCode(65 + idx), texto: map[k] }));
            } else {
              opcoes = entries
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([letra, texto]) => ({ letra, texto }));
            }
            opcoes = reindexLetters(opcoes);
          }

          // Config de escala (se existir)
          let scale = undefined;
          if (q.type === 'scale') {
            const rawScale = q.scale && typeof q.scale === 'object' ? q.scale : {};
            const rawMin = Number(rawScale.min);
            const rawMax = Number(rawScale.max);
            const min = Number.isFinite(rawMin) ? rawMin : DEFAULT_SCALE.min;
            const max = Number.isFinite(rawMax) ? rawMax : DEFAULT_SCALE.max;
            const labels = rawScale.labels && typeof rawScale.labels === 'object' ? rawScale.labels : {};
            scale = { min, max, labels };
          }

          return {
            id: qid,
            enunciado: q.question || '',
            tipo: q.type, // 'closed' | 'open' | 'priority' | 'scale'
            opcoes: (q.type === 'open' || q.type === 'scale') ? [] : opcoes,
            ...(q.type === 'scale' ? { scale } : {}),
            ...(q.type === 'open' ? { hasDate: !!q.hasDate } : {}), // NOVO: flag de data na pergunta aberta
          };
        });

        const produtosSel = resProdutos.success
          ? resProdutos.produtosTeste.map(p => p.idProduto)
          : [];

        setFormData({
          nome: resTeste.data.nome || '',
          descricao: resTeste.data.descricao || '',
          produtos: produtosSel,
          perguntas
        });

      } catch {
        showToast("Erro ao carregar os dados do teste", 4000, 'error');
        navigate('/testes');
      } finally {
        hideLoader();
      }
    };

    carregarTeste();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      setScrollBottom(nearBottom);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAddPergunta = () => {
    const newId = genUniqueId(currentQuestionIds());
    setFormData(prev => ({
      ...prev,
      perguntas: [
        ...prev.perguntas,
        {
          id: newId,
          enunciado: '',
          tipo: 'closed',
          opcoes: [
            { letra: 'A', texto: '' },
            { letra: 'B', texto: '' }
          ]
        }
      ]
    }));
  };

  const handleRemovePergunta = (index) => {
    setFormData(prev => ({
      ...prev,
      perguntas: prev.perguntas.filter((_, i) => i !== index)
    }));
  };

  const handleChangePergunta = (index, field, value) => {
    const novas = [...formData.perguntas];
    const atual = { ...novas[index] };

    // Garante que perguntas legadas sempre tenham id
    if (!atual.id || typeof atual.id !== 'string' || !atual.id.trim()) {
      const used = new Set(novas.map(p => p.id).filter(Boolean));
      atual.id = genUniqueId(used);
    }

    if (field === 'tipo') {
      atual.tipo = value; // 'closed' | 'open' | 'priority' | 'scale'

      if (value === 'open') {
        atual.opcoes = [];
        // garante flag de data com default false
        if (typeof atual.hasDate !== 'boolean') {
          atual.hasDate = false;
        }
        // pergunta aberta não usa escala
        if (atual.scale) {
          delete atual.scale;
        }
      } else if (value === 'scale') {
        atual.opcoes = [];
        if (!atual.scale) {
          atual.scale = { ...DEFAULT_SCALE };
        }
        // escala não usa campo de data
        if (typeof atual.hasDate !== 'undefined') {
          delete atual.hasDate;
        }
      } else {
        // closed/priority: garante ao menos 2 opções e normaliza letras
        if (!atual.opcoes || atual.opcoes.length < 2) {
          atual.opcoes = [{ letra: 'A', texto: '' }, { letra: 'B', texto: '' }];
        } else {
          atual.opcoes = reindexLetters(atual.opcoes);
        }
        // esses tipos não usam data nem escala customizada
        if (typeof atual.hasDate !== 'undefined') {
          delete atual.hasDate;
        }
        if (atual.scale) {
          delete atual.scale;
        }
      }
    } else {
      // campos genéricos, inclusive hasDate
      atual[field] = value;
    }

    novas[index] = atual;
    setFormData({ ...formData, perguntas: novas });
  };

  const handleChangeOpcao = (iPergunta, iOpcao, campo, valor) => {
    setFormData(prev => {
      const perguntas = [...prev.perguntas];
      const p = { ...perguntas[iPergunta] };

      // Garante id mesmo em legado ao tocar opções
      if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
        const used = new Set(perguntas.map(pp => pp.id).filter(Boolean));
        p.id = genUniqueId(used);
      }

      p.opcoes = [...(p.opcoes || [])];
      p.opcoes[iOpcao] = { ...p.opcoes[iOpcao], [campo]: valor };
      // Em priority, manter letras sequenciais é apenas visual (ordem será definida na resposta)
      p.opcoes = reindexLetters(p.opcoes);
      perguntas[iPergunta] = p;
      return { ...prev, perguntas };
    });
  };

  const handleAddOpcao = (iPergunta) => {
    setFormData(prev => {
      const perguntas = [...prev.perguntas];
      const p = { ...perguntas[iPergunta] };

      // Garante id mesmo em legado ao adicionar opção
      if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
        const used = new Set(perguntas.map(pp => pp.id).filter(Boolean));
        p.id = genUniqueId(used);
      }

      p.opcoes = [...(p.opcoes || [])];

      if (p.opcoes.length >= 26) {
        showToast("Limite de 26 opções atingido (A-Z)", 3000, 'warning');
        return prev;
      }

      p.opcoes = reindexLetters(p.opcoes);
      const L = nextAvailableLetter(p.opcoes) ?? String.fromCharCode(65 + p.opcoes.length);
      p.opcoes.push({ letra: L, texto: '' });
      p.opcoes = reindexLetters(p.opcoes);

      perguntas[iPergunta] = p;
      return { ...prev, perguntas };
    });
  };

  /** Escala: alterar mínimo/máximo */
  const handleChangeScaleRange = (iPergunta, bound, rawValue) => {
    const val = Number(rawValue);
    if (!Number.isFinite(val)) return;

    setFormData(prev => {
      const perguntas = [...prev.perguntas];
      const p = { ...perguntas[iPergunta] };
      const oldScale = p.scale || { ...DEFAULT_SCALE };

      let min = bound === 'min' ? val : Number(oldScale.min ?? DEFAULT_SCALE.min);
      let max = bound === 'max' ? val : Number(oldScale.max ?? DEFAULT_SCALE.max);

      if (!Number.isFinite(min)) min = DEFAULT_SCALE.min;
      if (!Number.isFinite(max)) max = DEFAULT_SCALE.max;

      // Garante min <= max
      if (min > max) {
        if (bound === 'min') {
          max = min;
        } else {
          min = max;
        }
      }

      const oldLabels = oldScale.labels && typeof oldScale.labels === 'object' ? oldScale.labels : {};
      const newLabels = Object.fromEntries(
        Object.entries(oldLabels).filter(([k, v]) => {
          const n = Number(k);
          return Number.isFinite(n) && n >= min && n <= max && (v || '').toString().trim().length > 0;
        })
      );

      p.scale = { min, max, labels: newLabels };
      perguntas[iPergunta] = p;
      return { ...prev, perguntas };
    });
  };

  /** Escala: alterar rótulo de um valor */
  const handleChangeScaleLabel = (iPergunta, value, text) => {
    setFormData(prev => {
      const perguntas = [...prev.perguntas];
      const p = { ...perguntas[iPergunta] };
      const oldScale = p.scale || { ...DEFAULT_SCALE };
      const oldLabels = oldScale.labels && typeof oldScale.labels === 'object' ? oldScale.labels : {};
      const labels = { ...oldLabels };

      const key = String(value);
      const trimmed = (text || '').trim();
      if (trimmed) {
        labels[key] = trimmed;
      } else {
        delete labels[key];
      }

      p.scale = { ...oldScale, labels };
      perguntas[iPergunta] = p;
      return { ...prev, perguntas };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nome = (formData.nome || '').trim();
    const descricao = (formData.descricao || '').trim();
    if (!nome || !descricao) {
      showToast("Preencha nome e descrição.", 3000, 'warning');
      return;
    }

    // Antes de salvar, garanta que TODAS as perguntas tenham id
    const perguntasComId = (formData.perguntas || []).map((pOriginal, idx, arr) => {
      const p = { ...pOriginal };
      if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
        const used = new Set(arr.map(px => px.id).filter(Boolean));
        p.id = genUniqueId(used);
      }
      return p;
    });

    const estrutura = {
      questions: perguntasComId.map(p => {
        const en = (p.enunciado || '').trim();

        if (p.tipo === 'open') {
          return {
            id: p.id,
            question: en,
            type: 'open',
            hasDate: !!p.hasDate, // NOVO: salva se a pergunta aberta tem data
          };
        }

        if (p.tipo === 'scale') {
          const rawScale = p.scale || DEFAULT_SCALE;
          const rawMin = Number(rawScale.min);
          const rawMax = Number(rawScale.max);
          let min = Number.isFinite(rawMin) ? rawMin : DEFAULT_SCALE.min;
          let max = Number.isFinite(rawMax) ? rawMax : DEFAULT_SCALE.max;

          if (min > max) {
            const tmp = min;
            min = max;
            max = tmp;
          }

          const srcLabels = rawScale.labels && typeof rawScale.labels === 'object' ? rawScale.labels : {};
          const labels = {};
          Object.entries(srcLabels).forEach(([k, v]) => {
            const n = Number(k);
            const txt = (v || '').toString().trim();
            if (
              Number.isInteger(n) &&
              n >= min &&
              n <= max &&
              txt
            ) {
              labels[String(n)] = txt;
            }
          });

          return {
            id: p.id,
            question: en,
            type: 'scale',
            scale: { min, max, labels }
          };
        }

        // closed / priority
        const opcoesNorm = reindexLetters(p.opcoes || []);
        const optionsObj = Object.fromEntries(opcoesNorm.map(o => [o.letra, (o.texto || '').trim()]));

        if (p.tipo === 'priority') {
          // Nesta página não definimos ordem; ela será escolhida pelo participante.
          return { id: p.id, question: en, type: 'priority', options: optionsObj };
        }

        return { id: p.id, question: en, type: 'closed', options: optionsObj };
      })
    };

    const payload = {
      nome,
      descricao,
      produtos: formData.produtos,
      estrutura: JSON.stringify(estrutura),
      ...(isEditing ? { id: idTeste } : {})
    };

    try {
      showLoader(isEditing ? "Atualizando teste" : "Criando teste");
      const response = isEditing ? await atualizarTeste(payload) : await criarTeste(payload);

      if (response.success) {
        const idDoTeste =
          isEditing
            ? idTeste
            : response.id;

        if (!idDoTeste) {
          showToast("Não foi possível obter o id do teste.", 4000, "error");
          return;
        }

        showToast(isEditing ? "Teste atualizado com sucesso!" : "Teste criado com sucesso!", 3000, "success");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate("/testes");
      } else {
        showToast(response.message || "Erro ao salvar teste", 4000, 'error');
      }
    } catch (error) {
      console.error("Erro ao conectar com o servidor:", error);
      showToast("Erro ao conectar com o servidor", 4000, 'error');
    } finally {
      hideLoader();
    }
  };

  const breadcrumbLabel = isEditing ? `${formData.nome || 'teste'}` : undefined;

  return (
    <div className={styles.TesteFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idTeste, label: breadcrumbLabel }] : []} />
        <h1 className={styles.title}>{isEditing ? "Editar Teste" : "Cadastro de Teste"}</h1>
        <FriendlyPhrase page={isEditing ? "atualizarTeste" : "adicionarTeste"} />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>

        <div className={styles.produtoSelector}>
          <h2 className={styles.sectionTitle}>Vincular Produtos</h2>

          {/* Pílulas de produtos selecionados */}
          <div className={styles.produtoPills}>
            {formData.produtos.map(id => {
              const produto = produtos.find(p => p.id === id);
              return (
                <div key={id} className={styles.produtoPill}>
                  {produto?.nome}
                  <span
                    className={styles.removerPill}
                    onClick={() =>
                      setFormData(prev => ({
                        ...prev,
                        produtos: prev.produtos.filter(pid => pid !== id)
                      }))
                    }
                  >
                    ×
                  </span>
                </div>
              );
            })}
          </div>

          {/* Barra de pesquisa */}
          <input
            type="text"
            value={buscaProduto}
            onChange={e => {
              const valor = e.target.value;
              setBuscaProduto(valor);
              const filtrados = produtos
                .filter(p =>
                  !formData.produtos.includes(p.id) &&
                  (p.nome || '').toLowerCase().includes(valor.toLowerCase())
                );
              setSugestoesFiltradas(filtrados);
            }}
            placeholder="Buscar e selecionar produtos"
            className={styles.produtoInput}
          />

          {/* Lista de sugestões */}
          {sugestoesFiltradas.length > 0 && (
            <ul className={styles.sugestoesLista}>
              {sugestoesFiltradas.map(prod => (
                <li
                  key={prod.id}
                  className={styles.sugestaoItem}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      produtos: [...prev.produtos, prod.id]
                    }));
                    setBuscaProduto('');
                    setSugestoesFiltradas([]);
                  }}
                >
                  {prod.nome}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.generalArea}>
          <h2 className={styles.sectionTitle}>Dados do Teste</h2>

          <label>Nome:
            <input type="text" name="nome" value={formData.nome} onChange={handleChange} required />
          </label>

          <label>Descrição:
            <button
              type="button"
              className={styles.descriptionEditorButton}
              onClick={() => setIsDescriptionEditorOpen(true)}
            >
              Abrir editor de descrição
            </button>

            <div className={styles.descriptionPreview}>
              {(formData.descricao || '').trim() ? (
                <div className={styles.descriptionPreviewMarkdown}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {formData.descricao}
                  </ReactMarkdown>
                </div>
              ) : (
                <span className={styles.descriptionPreviewEmpty}>
                  Nenhuma descricao cadastrada.
                </span>
              )}
            </div>
          </label>
        </div>

        <div className={styles.perguntasArea}>
          <h2 className={styles.questionTitle}>Questões</h2>

          {formData.perguntas.map((p, i) => {
            const meta = TYPE_META[p.tipo] || TYPE_META.closed;

            // Dados de escala (caso seja tipo escala)
            const scaleCfg = p.scale || DEFAULT_SCALE;
            const rawMin = Number(scaleCfg.min);
            const rawMax = Number(scaleCfg.max);
            let scaleMin = Number.isFinite(rawMin) ? rawMin : DEFAULT_SCALE.min;
            let scaleMax = Number.isFinite(rawMax) ? rawMax : DEFAULT_SCALE.max;
            if (scaleMin > scaleMax) {
              const tmp = scaleMin;
              scaleMin = scaleMax;
              scaleMax = tmp;
            }
            const scaleLabels = scaleCfg.labels || {};
            const scaleValues = [];
            for (let v = scaleMin; v <= scaleMax; v++) {
              scaleValues.push(v);
            }

            return (
              <div
                key={p.id || i}
                className={styles.perguntaCard}
                style={{ borderLeftColor: meta.border, background: meta.bg }}
              >
                <span className={styles.typeBadge} style={{ background: meta.badgeBg }}>
                  {meta.badgeText}
                </span>

                <label>Enunciado:
                  <input
                    type="text"
                    value={p.enunciado}
                    onChange={e => handleChangePergunta(i, 'enunciado', e.target.value)}
                    required
                  />
                </label>

                <label>Tipo:
                  <select
                    value={p.tipo}
                    onChange={e => handleChangePergunta(i, 'tipo', e.target.value)}
                    className={styles.selectInput}
                  >
                    {ORDERED_TYPES.map(t => (
                      <option key={t} value={t}>{TYPE_META[t].label}</option>
                    ))}
                  </select>
                </label>

                {/* CLOSED */}
                {p.tipo === 'closed' && (
                  <div>
                    <p className={styles.mutedNote}>
                      O participante escolherá uma das opções.
                    </p>

                    {p.opcoes.map((op, j) => (
                      <div key={j} className={styles.opcaoRow}>
                        <span className={styles.opcaoLetra}>{op.letra}</span>
                        <input
                          type="text"
                          placeholder="Texto da opção"
                          value={op.texto}
                          onChange={e => handleChangeOpcao(i, j, 'texto', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className={styles.removerOpBtn}
                          onClick={() => {
                            setFormData(prev => {
                              const perguntas = [...prev.perguntas];
                              const pLocal = { ...perguntas[i], opcoes: [...perguntas[i].opcoes] };
                              pLocal.opcoes.splice(j, 1);
                              perguntas[i] = pLocal;
                              return { ...prev, perguntas };
                            });
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <button type="button" className={styles.addQuestionBtn} onClick={() => handleAddOpcao(i)}>
                      Adicionar Opção
                    </button>
                  </div>
                )}

                {/* PRIORITY (sem ordenação aqui; apenas definir opções) */}
                {p.tipo === 'priority' && (
                  <div>
                    <p className={styles.mutedNote}>
                      O participante colocará as opções em ordem de prioridade.
                    </p>

                    {p.opcoes.map((op, j) => (
                      <div key={j} className={styles.opcaoRow}>
                        <span className={styles.opcaoLetra}>{op.letra}</span>
                        <input
                          type="text"
                          placeholder="Texto da opção (será ordenada na resposta)"
                          value={op.texto}
                          onChange={e => handleChangeOpcao(i, j, 'texto', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className={styles.removerOpBtn}
                          onClick={() => {
                            setFormData(prev => {
                              const perguntas = [...prev.perguntas];
                              const pLocal = { ...perguntas[i], opcoes: [...perguntas[i].opcoes] };
                              pLocal.opcoes.splice(j, 1);
                              pLocal.opcoes = reindexLetters(pLocal.opcoes);
                              perguntas[i] = pLocal;
                              return { ...prev, perguntas };
                            });
                          }}
                          title="Remover opção"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <button type="button" className={styles.addQuestionBtn} onClick={() => handleAddOpcao(i)}>
                      Adicionar Opção
                    </button>
                  </div>
                )}

                {/* ESCALA */}
                {p.tipo === 'scale' && (
                  <div>
                    <p className={styles.mutedNote}>
                      O participante escolherá um valor em uma escala numérica. Ajuste o intervalo e,
                      se quiser, defina rótulos para cada ponto da escala.
                    </p>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                      <label>Valor mínimo:
                        <input
                          type="number"
                          value={scaleMin}
                          onChange={e => handleChangeScaleRange(i, 'min', e.target.value)}
                          style={{ width: 120, marginLeft: 8 }}
                        />
                      </label>

                      <label>Valor máximo:
                        <input
                          type="number"
                          value={scaleMax}
                          onChange={e => handleChangeScaleRange(i, 'max', e.target.value)}
                          style={{ width: 120, marginLeft: 8 }}
                        />
                      </label>
                    </div>

                    <div>
                      <p className={styles.mutedNote}>
                        Rótulos (opcionais) para cada valor da escala:
                      </p>
                      {scaleValues.map(v => (
                        <div key={v} className={styles.opcaoRow}>
                          <span className={styles.opcaoLetra}>{v}</span>
                          <input
                            type="text"
                            placeholder={`Rótulo para ${v} (opcional)`}
                            value={scaleLabels[String(v)] || ''}
                            onChange={e => handleChangeScaleLabel(i, v, e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ABERTA */}
                {p.tipo === 'open' && (
                  <div>
                    <p className={styles.mutedNote}>
                      O participante digitará a resposta.
                    </p>

                    <label className={styles.mutedNote}>
                      <input
                        type="checkbox"
                        checked={!!p.hasDate}
                        onChange={e => handleChangePergunta(i, 'hasDate', e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      Incluir campo de data na resposta
                    </label>
                  </div>
                )}

                <button type="button" className={styles.removerBtn} onClick={() => handleRemovePergunta(i)}>
                  Remover questão
                </button>
              </div>
            );
          })}

          <button type="button" onClick={handleAddPergunta}>+ Adicionar Questão</button>
        </div>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Teste' : 'Salvar Teste'}
          </button>
        </div>
      </form>

      <button
        type="button"
        className={styles.scrollToBottomBtn}
        onClick={() =>
          scrollBottom
            ? window.scrollTo({ top: 0, behavior: 'smooth' })
            : window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
        }
        title={scrollBottom ? 'Ir para o topo' : 'Ir para o final'}
      >
        {scrollBottom ? <FaArrowUp /> : <FaArrowDown />}
      </button>

      <RichTextEditorModal
        isOpen={isDescriptionEditorOpen}
        title="Descricao do teste"
        value={formData.descricao}
        helperText="Este campo usa markdown e sera exibido formatado na tela de resposta do teste."
        confirmLabel="Aplicar descrição"
        onClose={() => setIsDescriptionEditorOpen(false)}
        onSave={(nextValue) => setFormData((prev) => ({ ...prev, descricao: nextValue }))}
      />
    </div>
  );
}

export default TesteFormPage;
